import type { UserProfile } from "../../types/auth";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";

/**
 * Whether this user can reach organization settings, which is admin-only in `App.tsx`.
 *
 * **Absent means allowed**, the same fail-open rule as `hasPermission`: a profile still loading is
 * indistinguishable from one with no role, and denying on absence would hide the route from every
 * admin during that window. The API enforces the real restriction either way.
 */
export const canReachOrganizationSettings = (profile: UserProfile | undefined): boolean => {
  if (!profile?.role) return true;
  return profile.role === "admin" || profile.role === "system_admin";
};

/**
 * Where a checklist task lives **in this client**.
 *
 * The mapping is here rather than in the API on purpose. A path returned by the server cannot be
 * checked against the router at compile time, so a renamed route would silently produce dead links,
 * and the paths would be wrong the day the mobile app grows a checklist of its own.
 *
 * **An unrecognised key returns null**, and the panel renders that row as plain text instead of a
 * link. The server may add a task before this client knows about it, and a row that reads as an
 * instruction is more useful than a link to nowhere.
 *
 * The permission-gated destinations are checked here as well as on the server. The server already
 * omits a task the caller cannot do, so this is belt and braces against that eligibility drifting
 * from the router — and it fails safe, to plain text rather than to a link straight into an
 * access-denied screen.
 */
export const getStartedRouteFor = (
  key: string,
  profile: UserProfile | undefined
): string | null => {
  switch (key) {
    case "add_player":
      return "/players";
    // Teams are created and managed from the players toolbar, so this is the same destination as
    // adding a player. /rosters is Roster Planning, which is a different feature behind its own
    // permission.
    case "create_team":
      return "/players";
    // Satisfied by either half, and the server sends this item to holders of notes OR calls, so
    // pick the one this coach can actually reach. `hasPermission` fails open on an absent
    // permissions list, which lands on Latest Notes — the same page an unrestricted coach gets.
    case "add_note_or_call":
      return hasPermission(profile, PERMISSIONS.notes) ? "/latest-notes" : "/calls";
    // The task is added on a phone, where the camera is. This links to the library so a coach who
    // meets the row on the web can at least see where the video will land, and it goes to plain
    // text for anyone whose admin withheld Video — the server omits the task for them anyway.
    case "add_video":
      return hasPermission(profile, PERMISSIONS.video) ? "/video" : null;
    case "add_schedule_event":
      return hasPermission(profile, PERMISSIONS.schedule) ? "/schedule" : null;
    case "invite_teammate":
      return canReachOrganizationSettings(profile) ? "/settings/users" : null;
    default:
      return null;
  }
};
